import type { AnalysisParameter, AnalysisParameterContract, AnalysisParameters } from '../types';

export interface AnalysisParameterIssue { key: string | null; message: string }
export interface AnalysisParameterValidation {
  params: AnalysisParameters | null;
  issues: AnalysisParameterIssue[];
}

export function parameterRange(parameter: AnalysisParameter): string {
  const { minimum, maximum, exclusive_minimum, exclusive_maximum } = parameter;
  if (parameter.kind === 'boolean') return 'true or false';
  const lower = minimum === null ? '' : `${minimum} ${exclusive_minimum ? '<' : '<='} `;
  const upper = maximum === null ? '' : ` ${exclusive_maximum ? '<' : '<='} ${maximum}`;
  return lower || upper ? `${lower}x${upper}` : 'any finite number';
}

function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  if (value !== null && typeof value === 'object') {
    return `{${Object.entries(value).sort(([left], [right]) => left < right ? -1 : left > right ? 1 : 0)
      .map(([key, entry]) => `${JSON.stringify(key)}:${canonicalJson(entry)}`).join(',')}}`;
  }
  return JSON.stringify(value) ?? '';
}

export function analysisOverrideUnchanged(text: string, baseline: AnalysisParameters): boolean {
  try {
    return canonicalJson(JSON.parse(text)) === canonicalJson(baseline);
  } catch {
    // An unfinished or invalid JSON draft is an unsaved edit, never a baseline.
    return false;
  }
}

export function validateAnalysisParameters(text: string, contract: AnalysisParameterContract | null): AnalysisParameterValidation {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch (error) {
    const reason = error instanceof Error ? error.message : String(error);
    const position = /unexpected end/i.test(reason) && !/position|line \d+ column/i.test(reason)
      ? ` (position ${text.length})` : '';
    return { params: null, issues: [{ key: null, message: `Invalid JSON: ${reason}${position}` }] };
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { params: null, issues: [{ key: null, message: 'Override JSON must be an object.' }] };
  }
  const issues: AnalysisParameterIssue[] = [];
  const params: AnalysisParameters = {};
  const parameters = new Map(contract?.parameters.map((parameter) => [parameter.key, parameter]) ?? []);
  for (const [key, candidate] of Object.entries(value)) {
    const parameter = parameters.get(key);
    if (!parameter) {
      issues.push({ key, message: `${key}: unknown parameter.` });
      continue;
    }
    const validKind = parameter.kind === 'boolean' ? typeof candidate === 'boolean'
      : typeof candidate === 'number' && Number.isFinite(candidate)
        && (parameter.kind !== 'integer' || Number.isInteger(candidate));
    if (!validKind) {
      issues.push({ key, message: `${key}: must be ${parameter.kind === 'integer' ? 'an integer' : `a ${parameter.kind}`}.` });
      continue;
    }
    if (typeof candidate === 'number' || typeof candidate === 'boolean') params[key] = candidate;
    if (typeof candidate === 'number' && (
      (parameter.minimum !== null && (parameter.exclusive_minimum ? candidate <= parameter.minimum : candidate < parameter.minimum))
      || (parameter.maximum !== null && (parameter.exclusive_maximum ? candidate >= parameter.maximum : candidate > parameter.maximum))
    )) issues.push({ key, message: `${key}: must satisfy ${parameterRange(parameter)}.` });
  }
  return { params: issues.length ? null : params, issues };
}
